import Vimeo from "@vimeo/player";
import Logger from "./logger";

/**
 * @param {object} config - Required config object.
 * * @param {string} config.name - Optional name for logging purposes
 * @param {string} config.mobile - Selector || Element that has video ID (data-video-mobile)
 * @param {string} config.desktop - Selector || Element that has video ID (data-video-desktop)
 * @param {string} [config.container] - Optional container element or selector for inline videos
 * @param {string} [config.launchBtn] - Launch button selector
 * @param {string} [config.hideElement] - Element to hide on launch (for when playing inline/not in modal)
 * @param {boolean} [config.preventBodyScroll = false] - Prevent body from scrolling (only applicable if modal), default is false
 * @param {object} vimeoOptions - Required options for Vimeo.
 * @param {boolean} [vimeoOptions.autopause]
 * @param {boolean} [vimeoOptions.autoplay]
 * @param {boolean} [vimeoOptions.background]
 * @param {boolean} [vimeoOptions.byline]
 * @param {string} [vimeoOptions.color]
 * @param {boolean} [vimeoOptions.controls]
 * @param {boolean} [vimeoOptions.dnt]
 * @param {number} [vimeoOptions.height]
 * @param {string} [vimeoOptions.interactive_params]
 * @param {boolean} [vimeoOptions.dnt]
 * @param {boolean} [vimeoOptions.keyboard]
 * @param {boolean} [vimeoOptions.loop]
 * @param {number} [vimeoOptions.maxheight]
 * @param {number} [vimeoOptions.maxwidth]
 * @param {boolean} [vimeoOptions.muted]
 * @param {boolean} [vimeoOptions.pip]
 * @param {boolean} [vimeoOptions.playsinline]
 * @param {boolean} [vimeoOptions.portrait]
 * @param {boolean} [vimeoOptions.responsive]
 * @param {boolean} [vimeoOptions.speed]
 * @param {string} [vimeoOptions.quality]
 * @param {string} [vimeoOptions.texttrack]
 * @param {boolean} [vimeoOptions.title]
 * @param {boolean} [vimeoOptions.transparent]
 * @param {number} [vimeoOptions.width]
 */

const mobileWidth = 650;

export default function VimeoPlayer(config, vimeoOptions) {
	const desktopID = 
		typeof config.desktop !== "string"
			? config.desktop.dataset.videoDesktop
			: document.querySelector(config.desktop).dataset.videoDesktop;
	if (!desktopID || desktopID === "") return;
	Logger.info(`new ${config.name} VimeoPlayer`);
	const mobileID = config.mobile ?
		(typeof config.mobile !== "string"
			? config.mobile.dataset.videoMobile
			: document.querySelector(config.mobile).dataset.videoMobile)
		: undefined;
	const launchBtn =
		typeof config.launchBtn !== "string"
			? config.launchBtn
			: document.querySelector(config.launchBtn);
	const container = config.container ?
		(typeof config.container !== "string"
			? config.container
			: document.querySelector(config.container)) : document.querySelector('#vimeo-container');
	const videoMobile = !config.container ? document.querySelector('#vimeo-mobile') : config.container;
	const videoDesktop = !config.container ? document.querySelector('#vimeo-desktop') : config.container;
	const closeBtn = !config.container ? document.querySelector('.vimeo-modal .vimeo-modal__close-btn') : undefined;
	const hideEl = config.hideElement ?
		(typeof config.hideElement !== "string"
			? config.hideElement
			: document.querySelector(config.hideElement))
		: undefined;
	
	const options = vimeoOptions || {};
	let playerDesktop;
	let playerMobile;
  let scrollY;

	const onLaunch = () => {
		if (!config.container && container) {
      scrollY = window.scrollY;
			container.dataset.show = true;
			if (config.preventBodyScroll) {
				document.body.dataset.modalMode = true;
				document.body.dataset.fixed = true;
				document.documentElement.dataset.modalMode = true;
			}
		}
		//
		if (mobileID && window.innerWidth < mobileWidth) {
			playerMobile = new Vimeo(videoMobile, {
				...options,
				id: mobileID,
			});
		} else {
			playerDesktop = new Vimeo(videoDesktop, {
				...options,
				id: desktopID,
			});
		}
		if (hideEl) hideEl.style.display = 'none';
		if (mobileID) window.addEventListener("resize", onResize);
	};

	const onResize = async () => {
		if (container && container.dataset.show === "false") return;
		let time = 0;
		if (window.innerWidth < mobileWidth && !playerMobile) {
			//
			if (playerDesktop) {
				time = await playerDesktop.getCurrentTime();
				playerDesktop.destroy();
				playerDesktop = null;
			}
			//
			playerMobile = new Vimeo(videoMobile, {
				...options,
				id: mobileID,
			});
			if (time) playerMobile.setCurrentTime(time);
		} else if (window.innerWidth >= mobileWidth && !playerDesktop) {
			//
			if (playerMobile) {
				time = await playerMobile.getCurrentTime();
				playerMobile.destroy();
				playerMobile = null;
			}
			//
			playerDesktop = new Vimeo(videoDesktop, {
				...options,
				id: desktopID,
			});
			if (time) playerDesktop.setCurrentTime(time);
		}
	};

	const onClose = () => {
		if (container) {
			container.dataset.show = false;
			if (config.preventBodyScroll) {
				document.body.dataset.modalMode = false;
				document.body.dataset.fixed = false;
				document.documentElement.dataset.modalMode = false;
			}
			window.scrollTo({ left: 0, top: scrollY, behavior: 'auto' });
			if (mobileID) window.removeEventListener("resize", onResize);
		}
		if (playerDesktop) {
			playerDesktop.destroy();
			playerDesktop = null;
		}
		if (playerMobile) {
			playerMobile.destroy();
			playerMobile = null;
		}
	};

	if (launchBtn) launchBtn.addEventListener("click", onLaunch);
	if (!config.container && closeBtn) closeBtn.addEventListener("click", onClose);
}
